import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CloudUpload, X, Video, CheckCircle2 } from "lucide-react";

import Button from "../../../../../components/buttons/Button";
import TextField from "../../../../../components/input/textfield/TextField";
import TextArea from "../../../../../components/input/textarea/TextArea";
import Select from "../../../../../components/input/select/Select";

import { useCreatorVideoUpload } from "../uploads/hooks/useCreatorVideoUpload";
import { generateUniqueId } from "../../../../../utils/helpers/generator/generateUniqueId";
import { s3Uploader } from "../../../../../utils/helpers/s3Uploader/s3Uploader";

import {
  getSetPriceAmount,
  validateVideoPriceAmount,
} from "./utils/helper/videoPricing";

import "./CreatorVideoUploadForm.css";

const VISIBILITY_OPTIONS = [
  { label: "Public", value: "public" },
  { label: "Unlisted", value: "unlisted" },
  { label: "Group", value: "group" },
  { label: "Private", value: "private" },
];

const SPORT_OPTIONS = [
  { label: "Rugby Union", value: "Rugby Union" },
  { label: "Rugby League", value: "Rugby League" },
  { label: "Other", value: "Other" },
];

const MAX_VIDEO_SIZE = 7 * 1024 ** 3;
const MIN_VIDEO_DURATION = 60;

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const formatDuration = (seconds) => {
  const total = Math.floor(Number(seconds) || 0);

  return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, "0")}s`;
};

const formatFileSize = (bytes) =>
  Number(bytes) >= 1024 ** 3
    ? `${(bytes / 1024 ** 3).toFixed(2)} GB`
    : `${(bytes / 1024 ** 2).toFixed(2)} MB`;

export default function CreatorVideoUploadForm({
  username,
  onClose,
  onUploadingChange,
  onSuccess,
}) {
  const {
    loading,
    error,
    setError,
    uploadingVideo,
    uploadingThumbnail,
    addCreatorVideo,
    getCreatorVideoUploadUrl,
    getCreatorVideoThumbnailUploadUrl,
  } = useCreatorVideoUpload(username);

  const [videoFile, setVideoFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [fileProcessing, setFileProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [priceTouched, setPriceTouched] = useState(false);

  const [formData, setFormData] = useState({
    videoTag: generateUniqueId(6),
    title: "",
    sport: "",
    description: "",
    visibility: "private",
    priceAmount: "",
  });

  const videoPreviewRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoUrlRef = useRef(null);
  const fileSelectionIdRef = useRef(0);
  const uploadInProgressRef = useRef(false);
  const mountedRef = useRef(true);

  const uploading =
    isUploading || loading || uploadingVideo || uploadingThumbnail;

  const isBusy = uploading || fileProcessing;

  const minimumPrice = getSetPriceAmount(videoFile?.duration);

  const priceValidation = validateVideoPriceAmount(
    formData.priceAmount,
    videoFile?.duration,
  );

  const priceError =
    priceTouched && videoFile && !priceValidation.valid
      ? priceValidation.message
      : "";

  const canSubmit =
    Boolean(username) &&
    Boolean(videoFile) &&
    Boolean(formData.title.trim()) &&
    Boolean(formData.sport) &&
    !isBusy &&
    priceValidation.valid;

  const displayError =
    uploadError || (typeof error === "string" ? error : error?.message || "");

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      fileSelectionIdRef.current += 1;

      if (videoUrlRef.current) {
        URL.revokeObjectURL(videoUrlRef.current);
        videoUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && !isBusy) {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isBusy, onClose]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handlePriceChange = (event) => {
    const value = typeof event === "string" ? event : event.target.value;

    if (value !== "" && !/^\d*(?:\.\d{0,2})?$/.test(value)) {
      return;
    }

    setFormData((previous) => ({
      ...previous,
      priceAmount: value,
    }));

    setPriceTouched(true);
    setUploadError("");
  };

  const processFile = async (file) => {
    if (!file || isBusy) return;

    setUploadError("");
    setError("");

    if (file.type !== "video/mp4") {
      setUploadError("Only MP4 video files are allowed.");
      return;
    }

    if (file.size <= 0 || file.size > MAX_VIDEO_SIZE) {
      setUploadError("Video must be under 7GB and cannot be empty.");
      return;
    }

    const selectionId = ++fileSelectionIdRef.current;
    const metadataUrl = URL.createObjectURL(file);
    const video = document.createElement("video");

    setFileProcessing(true);

    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;

    try {
      const duration = await new Promise((resolve, reject) => {
        video.onloadedmetadata = () => {
          const seconds = Number(video.duration);

          if (!Number.isFinite(seconds) || seconds <= 0) {
            reject(new Error("Unable to read video duration."));
            return;
          }

          resolve(seconds);
        };

        video.onerror = () => {
          reject(new Error("The selected video could not be read."));
        };

        video.src = metadataUrl;
      });

      if (!mountedRef.current || selectionId !== fileSelectionIdRef.current) {
        return;
      }

      if (duration < MIN_VIDEO_DURATION) {
        setUploadError("Videos must be at least 60 seconds long.");
        return;
      }

      const calculatedPrice = getSetPriceAmount(duration);

      if (calculatedPrice === null) {
        setUploadError("Unable to calculate the minimum video price.");
        return;
      }

      const previewUrl = URL.createObjectURL(file);

      if (videoUrlRef.current) {
        URL.revokeObjectURL(videoUrlRef.current);
      }

      videoUrlRef.current = previewUrl;

      setVideoFile({
        file,
        type: file.type,
        size: file.size,
        duration,
        filename: `${generateUniqueId()}.mp4`,
        url: previewUrl,
      });

      setFormData((previous) => ({
        ...previous,
        title: previous.title.trim() || file.name.replace(/\.[^.]+$/, ""),
        priceAmount: calculatedPrice.toFixed(2),
      }));

      setPriceTouched(false);
      setProgress(0);
    } catch (err) {
      if (mountedRef.current && selectionId === fileSelectionIdRef.current) {
        setUploadError(
          getErrorMessage(err, "Unable to process the selected video."),
        );
      }
    } finally {
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(metadataUrl);

      if (mountedRef.current && selectionId === fileSelectionIdRef.current) {
        setFileProcessing(false);
      }
    }
  };

  const removeVideoFile = () => {
    if (isBusy) return;

    fileSelectionIdRef.current += 1;

    if (videoUrlRef.current) {
      URL.revokeObjectURL(videoUrlRef.current);
      videoUrlRef.current = null;
    }

    setVideoFile(null);
    setProgress(0);
    setUploadError("");
    setPriceTouched(false);

    setFormData((previous) => ({
      ...previous,
      priceAmount: "",
    }));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const captureThumbnail = () =>
    new Promise((resolve, reject) => {
      const video = videoPreviewRef.current;
      const canvas = canvasRef.current;

      if (!video || !canvas || video.readyState < 1) {
        reject(new Error("Video preview is not ready."));
        return;
      }

      const context = canvas.getContext("2d");

      if (!context) {
        reject(new Error("Unable to create thumbnail."));
        return;
      }

      const createThumbnail = () => {
        if (!video.videoWidth || !video.videoHeight) {
          reject(new Error("Video dimensions are unavailable."));
          return;
        }

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        try {
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
        } catch (err) {
          reject(err);
          return;
        }

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Thumbnail generation failed."));
              return;
            }

            const filename = `${generateUniqueId()}.jpg`;

            resolve(
              new File([blob], filename, {
                type: "image/jpeg",
              }),
            );
          },
          "image/jpeg",
          0.7,
        );
      };

      const thumbnailTime = Math.min(2, Math.max(video.duration - 0.1, 0));

      if (
        video.readyState >= 2 &&
        Math.abs(video.currentTime - thumbnailTime) < 0.05
      ) {
        createThumbnail();
        return;
      }

      const cleanup = () => {
        video.removeEventListener("seeked", handleSeeked);
        video.removeEventListener("error", handleError);
      };

      const handleSeeked = () => {
        cleanup();
        createThumbnail();
      };

      const handleError = () => {
        cleanup();
        reject(new Error("Unable to capture video thumbnail."));
      };

      video.addEventListener("seeked", handleSeeked, { once: true });
      video.addEventListener("error", handleError, { once: true });

      try {
        video.currentTime = thumbnailTime;
      } catch (err) {
        cleanup();
        reject(err);
      }
    });

  const validateUpload = () => {
    if (!username) {
      setUploadError("Creator username is required.");
      return false;
    }

    if (!formData.title.trim() || !formData.sport || !formData.visibility) {
      setUploadError("Please complete the required fields.");
      return false;
    }

    if (
      !videoFile?.file ||
      !videoFile.filename ||
      !videoFile.duration ||
      !videoFile.size
    ) {
      setUploadError("Please select a valid video.");
      return false;
    }

    if (!priceValidation.valid) {
      setPriceTouched(true);
      setUploadError(priceValidation.message);
      return false;
    }

    return true;
  };

  const startUpload = async () => {
    if (isBusy || uploadInProgressRef.current) return;

    if (!validateUpload()) return;

    uploadInProgressRef.current = true;

    setIsUploading(true);
    setUploadError("");
    setError("");
    setProgress(0);

    onUploadingChange?.(true);

    try {
      const thumbnailFile = await captureThumbnail();

      setProgress(5);

      const [videoUploadData, thumbnailUploadData] = await Promise.all([
        getCreatorVideoUploadUrl(),
        getCreatorVideoThumbnailUploadUrl(),
      ]);

      if (!videoUploadData || !thumbnailUploadData) {
        throw new Error("Unable to prepare video upload.");
      }

      await s3Uploader(
        videoUploadData,
        {
          file: videoFile.file,
          filename: videoFile.filename,
        },
        0,
        (_index, percent) => {
          if (!mountedRef.current) return;

          const safePercent = Math.min(100, Math.max(0, Number(percent) || 0));

          setProgress(Math.round(5 + safePercent * 0.85));
        },
      );

      if (mountedRef.current) setProgress(90);

      await s3Uploader(thumbnailUploadData, {
        file: thumbnailFile,
        filename: thumbnailFile.name,
      });

      if (mountedRef.current) setProgress(95);

      const finalPrice = validateVideoPriceAmount(
        formData.priceAmount,
        videoFile.duration,
      );

      if (!finalPrice.valid) {
        throw new Error(finalPrice.message);
      }

      const result = await addCreatorVideo({
        videoTag: formData.videoTag,
        title: formData.title.trim(),
        sport: formData.sport,
        description: formData.description.trim(),
        visibility: formData.visibility,
        priceAmount: finalPrice.priceAmount,
        thumbnailKey: thumbnailFile.name,
        videoKey: videoFile.filename,
        videoSize: videoFile.size,
        videoType: videoFile.type,
        videoDuration: videoFile.duration,
      });

      if (!result?.success) {
        throw new Error(
          result?.message || "Unable to save the uploaded video.",
        );
      }

      if (mountedRef.current) {
        setProgress(100);
      }

      onSuccess?.(result);
      onClose?.();
    } catch (err) {
      console.error("Creator video upload failed:", err);

      if (mountedRef.current) {
        setUploadError(
          getErrorMessage(err, "An error occurred during upload."),
        );
      }
    } finally {
      uploadInProgressRef.current = false;

      if (mountedRef.current) {
        setIsUploading(false);
      }

      onUploadingChange?.(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    void startUpload();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);

    if (!isBusy) {
      void processFile(event.dataTransfer?.files?.[0]);
    }
  };

  const progressLabel =
    progress < 90
      ? "Uploading video"
      : progress < 95
        ? "Uploading thumbnail"
        : progress < 100
          ? "Saving video"
          : "Upload complete";

  return createPortal(
    <form
      className="creator-video-upload-form"
      onSubmit={handleSubmit}
      aria-label="Upload video"
    >
      <header className="creator-video-upload-form__header">
        <div className="creator-video-upload-form__header-info">
          <h2>Upload Video</h2>
          <p>Upload and publish your video on Katch.</p>
        </div>

        <button
          type="button"
          className="creator-video-upload-form__close"
          onClick={() => onClose?.()}
          disabled={isBusy}
          aria-label="Close video upload"
        >
          <X size={22} />
        </button>
      </header>

      <div className="creator-video-upload-form__workspace">
        <main className="creator-video-upload-form__main">
          <div className="creator-video-upload-form__toolbar">
            <div>
              <h3>Video Preview</h3>
              <p>Upload an MP4 video, up to 7GB.</p>
            </div>
          </div>

          {!videoFile ? (
            <div
              className={`creator-video-upload-form__dropzone ${
                isDragging ? "creator-video-upload-form__dropzone--active" : ""
              }`}
              role="button"
              tabIndex={isBusy ? -1 : 0}
              aria-disabled={isBusy}
              onDragOver={(event) => {
                event.preventDefault();
                if (!isBusy) setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => {
                if (!isBusy) fileInputRef.current?.click();
              }}
              onKeyDown={(event) => {
                if ((event.key === "Enter" || event.key === " ") && !isBusy) {
                  event.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
            >
              <div className="creator-video-upload-form__upload-icon">
                <CloudUpload size={32} strokeWidth={1.5} />
              </div>

              <strong>
                {fileProcessing ? "Processing video..." : "Choose an MP4 video"}
              </strong>

              <span>
                {fileProcessing
                  ? "Reading video metadata"
                  : "Click to browse or drag and drop"}
              </span>

              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,.mp4"
                disabled={isBusy}
                hidden
                onChange={(event) => {
                  void processFile(event.target.files?.[0]);
                }}
              />
            </div>
          ) : (
            <div className="creator-video-upload-form__preview">
              <div className="creator-video-upload-form__player">
                <video
                  ref={videoPreviewRef}
                  src={videoFile.url}
                  controls
                  playsInline
                  preload="auto"
                  controlsList="nodownload"
                  disablePictureInPicture
                  onContextMenu={(event) => event.preventDefault()}
                />

                <canvas ref={canvasRef} hidden />

                {!isBusy && (
                  <button
                    type="button"
                    className="creator-video-upload-form__remove"
                    onClick={removeVideoFile}
                    aria-label="Remove video"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>

              <div className="creator-video-upload-form__file">
                <span className="creator-video-upload-form__file-icon">
                  <Video size={20} />
                </span>

                <div className="creator-video-upload-form__file-info">
                  <strong>{videoFile.file.name}</strong>
                  <span>
                    {formatDuration(videoFile.duration)} ·{" "}
                    {formatFileSize(videoFile.size)}
                  </span>
                </div>

                <CheckCircle2
                  size={20}
                  className="creator-video-upload-form__file-success"
                />
              </div>
            </div>
          )}

          {uploading && (
            <div
              className="creator-video-upload-form__progress"
              role="status"
              aria-live="polite"
            >
              <div className="creator-video-upload-form__progress-header">
                <span>{progressLabel}</span>
                <strong>{progress}%</strong>
              </div>

              <div
                className="creator-video-upload-form__progress-track"
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Video upload progress"
              >
                <div
                  className="creator-video-upload-form__progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </main>

        <aside className="creator-video-upload-form__sidebar">
          <div className="creator-video-upload-form__sidebar-header">
            <h3>Video Details</h3>
            <p>Provide information about your video.</p>
          </div>

          <div className="creator-video-upload-form__fields">
            <TextField
              name="title"
              label="Title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Match name or highlight title"
              required
              disabled={isBusy}
            />

            <Select
              name="sport"
              label="Sport"
              value={formData.sport}
              onChange={handleInputChange}
              items={SPORT_OPTIONS}
              defaultOption="Select sport"
              required
              disabled={isBusy}
            />

            <Select
              name="visibility"
              label="Visibility"
              value={formData.visibility}
              onChange={handleInputChange}
              items={VISIBILITY_OPTIONS}
              required
              disabled={isBusy}
            />

            <TextArea
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Add context for viewers, players or scouts"
              maxLength={500}
              disabled={isBusy}
            />
          </div>

          <div className="creator-video-upload-form__pricing">
            <div className="creator-video-upload-form__sidebar-header">
              <h3>Rental Pricing</h3>
              <p>Set the rental price for your video.</p>
            </div>

            <TextField
              name="priceAmount"
              type="number"
              label="Rental Price (AUD)"
              min={minimumPrice ?? undefined}
              step="0.01"
              inputMode="decimal"
              value={formData.priceAmount}
              onChange={handlePriceChange}
              onBlur={() => setPriceTouched(true)}
              placeholder={
                minimumPrice === null
                  ? "Select a video"
                  : `Minimum A$${minimumPrice.toFixed(2)}`
              }
              required
              disabled={!videoFile || isBusy}
            />

            <p className="creator-video-upload-form__price-help">
              {minimumPrice === null
                ? "Choose a video to calculate the minimum rental price."
                : `Minimum rental price: A$${minimumPrice.toFixed(2)}.`}
            </p>

            {priceError && (
              <p className="creator-video-upload-form__error">{priceError}</p>
            )}
          </div>

          {videoFile && (
            <div className="creator-video-upload-form__summary">
              <h4>Video Summary</h4>

              <div>
                <span>Duration</span>
                <strong>{formatDuration(videoFile.duration)}</strong>
              </div>

              <div>
                <span>File size</span>
                <strong>{formatFileSize(videoFile.size)}</strong>
              </div>

              <div>
                <span>Visibility</span>
                <strong>{formData.visibility}</strong>
              </div>
            </div>
          )}
        </aside>
      </div>

      <footer className="creator-video-upload-form__footer">
        <div className="creator-video-upload-form__footer-status">
          {displayError ? (
            <span className="creator-video-upload-form__error" role="alert">
              {displayError}
            </span>
          ) : uploading ? (
            <span>
              {progressLabel} · {progress}%
            </span>
          ) : fileProcessing ? (
            <span>Processing video...</span>
          ) : null}
        </div>

        <div className="creator-video-upload-form__actions">
          <Button
            type="button"
            value="Cancel"
            variant="primary-outlined"
            onClick={() => onClose?.()}
            disabled={isBusy}
          />

          <Button
            type="submit"
            value={uploading ? `Uploading ${progress}%` : "Publish Video"}
            variant="primary"
            disabled={!canSubmit}
          />
        </div>
      </footer>
    </form>,
    document.body,
  );
}
